<template>
    <section class="dp-layout theme-meeting-summary-page">
        <section class="dp-left">
            <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1"></DepartmentPane>
        </section>

        <section class="dp-right">
            <template v-if="schoolId > 0">
                <div class="filter-bar">
                    <el-form :inline="true" size="small">
                        <el-form-item label="年月">
                            <el-date-picker
                                v-model="filterForm.yearMonth"
                                type="month"
                                placeholder="选择月份"
                                value-format="yyyy-MM"
                                style="width: 140px;"
                                @change="handleSearch"
                            />
                        </el-form-item>
                        <el-form-item label="校区">
                            <el-select v-model="filterForm.campusId" placeholder="请选择校区" clearable style="width: 140px;" @change="handleSearch">
                                <el-option v-for="item in campusOptions" :key="item.Id" :label="item.Name" :value="item.Id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="状态">
                            <el-select v-model="filterForm.completionFilter" placeholder="全部" clearable style="width: 140px;" @change="handleSearch">
                                <el-option label="未交" value="missing" />
                                <el-option label="已交" value="submitted" />
                                <el-option label="未启用/未配置" value="disabled" />
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
                            <el-button
                                v-if="hasActionPermission('handleExport')"
                                type="success"
                                icon="el-icon-download"
                                :loading="exporting"
                                @click="handleExport"
                            >导出</el-button>
                        </el-form-item>
                    </el-form>
                </div>

                <div class="stat-cards" v-loading="loading">
                    <div class="stat-card stat-card--danger">
                        <div class="stat-card__num">{{ summary.MissingCount || 0 }}</div>
                        <div class="stat-card__label">未交班级</div>
                    </div>
                    <div class="stat-card stat-card--success">
                        <div class="stat-card__num">{{ summary.SubmittedCount || 0 }}</div>
                        <div class="stat-card__label">已交班级</div>
                    </div>
                    <div class="stat-card">
                        <div class="stat-card__num">{{ summary.DisabledCount || 0 }}</div>
                        <div class="stat-card__label">未启用/未配置</div>
                    </div>
                </div>

                <div class="tbl-scroll-host">
                    <el-table :data="summary.Items || []" v-loading="loading" border height="100%" class="compact-table summary-table">
                        <el-table-column type="index" label="#" width="56" align="center" :index="indexMethod" />
                        <el-table-column prop="CampusName" label="校区" min-width="140" show-overflow-tooltip />
                        <el-table-column prop="ClassName" label="班级" min-width="160" show-overflow-tooltip />
                        <el-table-column label="状态" width="120" align="center">
                            <template slot-scope="scope">
                                <el-tag :type="statusTagType(scope.row.CompletionStatus)" size="mini">
                                    {{ scope.row.CompletionStatusText }}
                                </el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="TeacherName" label="提交教师" min-width="110" show-overflow-tooltip>
                            <template slot-scope="scope">{{ scope.row.TeacherName || '-' }}</template>
                        </el-table-column>
                        <el-table-column label="提交时间" min-width="160" align="center">
                            <template slot-scope="scope">{{ formatDateTime(scope.row.SubmitTime) }}</template>
                        </el-table-column>
                        <el-table-column label="加分" width="90" align="center">
                            <template slot-scope="scope">
                                <span v-if="scope.row.Score != null">+{{ formatScore(scope.row.Score) }}</span>
                                <span v-else class="muted">-</span>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <div class="pager">
                    <el-pagination
                        background
                        layout="total, sizes, prev, pager, next"
                        :total="total"
                        :current-page.sync="page"
                        :page-size.sync="pageSize"
                        :page-sizes="[10, 20, 50, 100]"
                        @current-change="loadSummary"
                        @size-change="handleSizeChange"
                    />
                </div>
            </template>

            <div v-else class="no-department-tip">
                <div class="no-department-tip__inner">
                    <i class="el-icon-school"></i>
                    <p>请在左侧选择所属学校，查看完成汇总</p>
                </div>
            </div>
        </section>
    </section>
</template>

<script>
import DepartmentPane from '../../components/DepartmentPane';
import { getButtonList } from '../../promissionRouter';
import { getCampusList } from '../../api/modules/misc';
import {
    getThemeClassMeetingCompletionSummary,
    exportThemeClassMeetingCompletionSummary
} from '../../api/modules/themeClassMeeting';
import { normalizeDeptId, deptIdsEqual, resolveActiveSchoolId } from '../../utils/deptCascaderProps';

export default {
    name: 'ThemeClassMeetingSummary',
    components: { DepartmentPane },
    data() {
        const now = new Date();
        const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        return {
            schoolId: 0,
            schoolName: '',
            boundSchoolId: 0,
            buttonList: [],
            loading: false,
            exporting: false,
            campusOptions: [],
            page: 1,
            pageSize: 10,
            total: 0,
            filterForm: {
                yearMonth: ym,
                campusId: null,
                completionFilter: 'missing'
            },
            summary: {
                YearMonth: ym,
                SubmittedCount: 0,
                MissingCount: 0,
                DisabledCount: 0,
                Items: []
            }
        };
    },
    created() {
        this.buttonList = getButtonList(this.$route.path) || [];
        const sid = this.resolveSchoolId();
        if (sid) this.applySchoolChange(sid, this.$store.state.activeSchoolName);
    },
    watch: {
        '$store.state.activeSchoolId'(newSchoolId) {
            this.applySchoolChange(newSchoolId, this.$store.state.activeSchoolName);
        }
    },
    methods: {
        hasActionPermission(func) {
            if (!this.buttonList || !this.buttonList.length) return true;
            return this.buttonList.some(button => button.Func === func);
        },

        /** 解析当前学校 Id（字符串，避免雪花 Id 被 Number 截断）。最后修改：2026-10-18 */
        resolveSchoolId() {
            const fromStore = resolveActiveSchoolId(this);
            if (fromStore) return fromStore;
            const local = normalizeDeptId(this.schoolId);
            if (local) return local;
            return '';
        },

        async applySchoolChange(schoolId, schoolName) {
            const id = normalizeDeptId(schoolId);
            if (!id) return;
            if (deptIdsEqual(id, this.schoolId) && deptIdsEqual(this.boundSchoolId, id)) return;
            this.schoolId = id;
            this.boundSchoolId = id;
            if (schoolName) this.schoolName = schoolName;
            this.page = 1;
            await this.loadCampusOptions(true);
            this.loadSummary();
        },

        treeNodeClick(data) {
            this.applySchoolChange(data && data.value, data && data.label);
        },

        indexMethod(index) {
            return (this.page - 1) * this.pageSize + index + 1;
        },

        handleSizeChange() {
            this.page = 1;
            this.loadSummary();
        },

        handleSearch() {
            this.page = 1;
            this.loadSummary();
        },

        /** 取校区列表第一项 Id，无校区返回 null */
        pickDefaultCampusId() {
            const first = (this.campusOptions || [])[0];
            if (!first) return null;
            const id = first.Id != null ? first.Id : first.id;
            return id != null ? id : null;
        },

        formatScore(v) {
            const n = Number(v);
            if (Number.isNaN(n)) return '-';
            return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
        },

        formatDateTime(v) {
            if (!v) return '-';
            const d = new Date(v);
            if (Number.isNaN(d.getTime())) return String(v).replace('T', ' ').slice(0, 19);
            const pad = n => (n < 10 ? '0' + n : '' + n);
            return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
        },

        statusTagType(status) {
            if (status === 'missing') return 'danger';
            if (status === 'submitted') return 'success';
            return 'info';
        },

        /**
         * 加载校区并默认选中第一个。创建于 2026-09-13
         * @param {boolean} forceDefault 是否强制覆盖当前选中校区
         */
        async loadCampusOptions(forceDefault = false) {
            if (!this.schoolId) return;
            try {
                const res = await getCampusList({ page: 1, intPageSize: 999, departmentId: this.schoolId });
                if (res.data && res.data.success) {
                    this.campusOptions = (res.data.response && res.data.response.data) || [];
                } else {
                    this.campusOptions = [];
                }
            } catch (e) {
                this.campusOptions = [];
            }
            const exists = this.campusOptions.some(c => String(c.Id != null ? c.Id : c.id) === String(this.filterForm.campusId));
            if (forceDefault || this.filterForm.campusId == null || !exists) {
                this.filterForm.campusId = this.pickDefaultCampusId();
            }
        },

        async loadSummary() {
            if (!this.schoolId || !this.filterForm.yearMonth) return;
            this.loading = true;
            try {
                const res = await getThemeClassMeetingCompletionSummary({
                    schoolId: this.schoolId,
                    yearMonth: this.filterForm.yearMonth,
                    campusId: this.filterForm.campusId || undefined,
                    completionFilter: this.filterForm.completionFilter || undefined,
                    page: this.page,
                    intPageSize: this.pageSize
                });
                if (res.data && res.data.success) {
                    const resp = res.data.response || {};
                    const items = resp.Items || resp.items || [];
                    this.summary = {
                        YearMonth: resp.YearMonth != null ? resp.YearMonth : resp.yearMonth,
                        SubmittedCount: Number(resp.SubmittedCount != null ? resp.SubmittedCount : (resp.submittedCount || 0)),
                        MissingCount: Number(resp.MissingCount != null ? resp.MissingCount : (resp.missingCount || 0)),
                        DisabledCount: Number(resp.DisabledCount != null ? resp.DisabledCount : (resp.disabledCount || 0)),
                        Items: items
                    };
                    // 与记录页一致：优先 dataCount（PageModel 风格）
                    const count = resp.dataCount != null ? resp.dataCount
                        : (resp.DataCount != null ? resp.DataCount : 0);
                    this.total = Number(count) || 0;
                } else {
                    this.summary.Items = [];
                    this.total = 0;
                    this.$message.error((res.data && res.data.msg) || '加载失败');
                }
            } catch (e) {
                this.summary.Items = [];
                this.total = 0;
                this.$message.error('加载失败');
            } finally {
                this.loading = false;
            }
        },

        /** 从 Blob 错误响应中读取后端提示 */
        readBlobMessage(blob) {
            return new Promise(resolve => {
                if (!blob) {
                    resolve('');
                    return;
                }
                const reader = new FileReader();
                reader.onload = () => {
                    const text = String(reader.result || '');
                    try {
                        const result = JSON.parse(text);
                        resolve(result.msg || result.message || text);
                    } catch (e) {
                        resolve(text);
                    }
                };
                reader.onerror = () => resolve('');
                reader.readAsText(blob);
            });
        },

        /** 按当前筛选导出完成汇总 Excel。创建于 2026-09-25 */
        async handleExport() {
            if (!this.schoolId || !this.filterForm.yearMonth) return;
            if (!this.total || this.total <= 0) {
                this.$message.warning('没有数据可导出');
                return;
            }
            this.exporting = true;
            try {
                const params = {
                    schoolId: this.schoolId,
                    yearMonth: this.filterForm.yearMonth,
                    campusId: this.filterForm.campusId || undefined,
                    completionFilter: this.filterForm.completionFilter || undefined
                };
                const response = await exportThemeClassMeetingCompletionSummary(params);
                if (!response.data) {
                    this.$message.error('导出数据为空');
                    return;
                }
                const contentType = String((response.headers &&
                    (response.headers['content-type'] || response.headers['Content-Type'])) || '').toLowerCase();
                const blob = response.data instanceof Blob ? response.data : new Blob([response.data], { type: contentType });
                if (!contentType.includes('spreadsheetml') && !contentType.includes('application/octet-stream')) {
                    const message = await this.readBlobMessage(blob);
                    this.$message.error(message || '导出失败');
                    return;
                }
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `主题班会完成汇总_${this.filterForm.yearMonth}_${Date.now()}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } catch (err) {
                if (!err || !err.bizMessage) {
                    this.$message.error('导出失败');
                }
            } finally {
                this.exporting = false;
            }
        }
    }
};
</script>

<style scoped>
.theme-meeting-summary-page .filter-bar {
    margin-bottom: 12px;
    flex: 0 0 auto;
}
.theme-meeting-summary-page .stat-cards {
    display: flex;
    gap: 12px;
    margin-bottom: 14px;
    flex: 0 0 auto;
}
.theme-meeting-summary-page .stat-card {
    flex: 1;
    background: #F5F7FA;
    border-radius: 6px;
    padding: 14px 16px;
}
.theme-meeting-summary-page .stat-card__num {
    font-size: 24px;
    font-weight: 700;
    color: #303133;
    line-height: 1.2;
}
.theme-meeting-summary-page .stat-card__label {
    margin-top: 4px;
    color: #909399;
    font-size: 13px;
}
.theme-meeting-summary-page .stat-card--danger {
    background: #FEF0F0;
}
.theme-meeting-summary-page .stat-card--danger .stat-card__num {
    color: #F56C6C;
}
.theme-meeting-summary-page .stat-card--success {
    background: #F0F9EB;
}
.theme-meeting-summary-page .stat-card--success .stat-card__num {
    color: #67C23A;
}
.theme-meeting-summary-page .muted {
    color: #C0C4CC;
}
.theme-meeting-summary-page .pager {
    margin-top: 12px;
    text-align: right;
    flex: 0 0 auto;
}
.theme-meeting-summary-page .summary-table {
    width: 100%;
}
.theme-meeting-summary-page .summary-table ::v-deep .el-table__body-wrapper {
    overflow-x: hidden !important;
}
.theme-meeting-summary-page .summary-table:not(.el-table--scrollable-y) ::v-deep .el-table__body-wrapper {
    overflow-y: hidden !important;
}
.no-department-tip {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.no-department-tip__inner {
    text-align: center;
    color: #909399;
}
.no-department-tip__inner i {
    font-size: 40px;
    margin-bottom: 12px;
}
</style>
